import { Text, TextInput, Textarea, Button, Group, SimpleGrid, createStyles, rem, Flex, ScrollArea } from "@mantine/core";
import { useForm } from "@mantine/form";
import t from "../../stores/language";
import { Icon } from "../../custom-components/icons";
import { faEnvelope, faMobilePhone } from "@fortawesome/pro-duotone-svg-icons";
import { useState } from "react";
import { useStore } from "../../stores/store";
import theme from "../../stores/theme";

const useStyles = createStyles((theme) => {
  const BREAKPOINT = theme.fn.smallerThan("sm");
  return {
    contactContainer: {
      display: "flex",
      backgroundColor: theme.colorScheme === "dark" ? theme.colors.dark[8] : theme.white,
      boxShadow: theme.other.arc.shadows.lg,
      padding: theme.other.arc.paddings[2],
      border: `${rem(1)} solid ${theme.colorScheme === "dark" ? theme.colors.dark[8] : theme.colors.gray[2]}`,
      flexDirection: "column",
      gap: theme.spacing.md,
    },

    form: {
      boxSizing: "border-box",
      minWidth: "50rem",
      marginLeft: "auto",
      marginRight: "auto",

      [BREAKPOINT]: {
        padding: theme.spacing.md,
      },
    },

    fields: {
      marginTop: rem(-12),
    },

    fieldInput: {
      flex: 1,
      "& + &": {
        marginLeft: theme.spacing.md,
        [BREAKPOINT]: {
          marginLeft: 0,
          marginTop: theme.spacing.md,
        },
      },
    },

    title: {
      padding: `${theme.other.arc.paddings[2]} ${theme.other.arc.paddings[0]}`,
      fontWeight: 500,
      marginBottom: theme.other.arc.margins[0],
      fontSize: theme.fontSizes.lg,
      textAlign: "center",
    },
    IconButton: {
      borderRadius: "50%",
      width: "55px",
      height: "55px",
      backgroundColor: "#e7f5ff",
      fontSize: theme.fontSizes.xl,
      padding: theme.other.arc.paddings[1],
      "&: not([data-disabled]):hover": {
        backgroundColor: "#e7f5ff",
      },
      cursor: "default",
    },
  };
});

export function GetInTouch() {
  const { authStore } = useStore();
  const { classes } = useStyles();
  const [sending, setSending] = useState<boolean>(false);
  const [error, setError] = useState<{ isError: boolean; message: string }>({ isError: false, message: "" });
  const form = useForm({
    initialValues: {
      name: authStore.users.find((d) => d.email === authStore.user?.profile?.email)?.name ?? "",
      email: authStore.user?.profile?.email ?? "",
      subject: "",
      message: "",
      phoneNumber: "",
    },
    validate: {
      name: (value) => (value.trim().length > 1 ? null : "Please enter atleast 2 character"),
      email: (val) => (/^\S+@\S+$/.test(val) ? null : "Invalid email"),
      subject: (value) => (value.trim().length !== 0 ? null : "Please enter subject"),
      phoneNumber: (value) => (value.trim().length > 8 ? null : "Please enter atleast 9 digits"),
    },
  });

  const sendEmail = async (values: typeof form.values) => {
    setSending(true);
    const result = await authStore.SendGetInTouchEmail(values);
    if (result) {
      setError({ isError: false, message: "Thank you for your message. It has been sent." });
    } else {
      setError({ isError: true, message: "Message not sent." });
    }
    setSending(false);
  };

  const resetState = () => {
    if (error.message !== "") {
      setError({ isError: false, message: "" });
    }
  };

  return (
    <div className={classes.contactContainer}>
      <div className={classes.form}>
        <Text className={classes.title}>{t("Let’s Create Something Together!")}</Text>
        <SimpleGrid cols={2} breakpoints={[{ maxWidth: "sm", cols: 1 }]}>
          <Group position="center">
            <Button size="lg" variant="subtle" className={classes.IconButton}>
              <Icon icon={faMobilePhone} />
            </Button>
            <div>
              <Text fw={500}>{t("Phone")}</Text>
              <Text color={"gray"}> 03 5955 5700</Text>
            </div>
          </Group>
          <Group position="center">
            <Button size="lg" variant="subtle" className={classes.IconButton}>
              <Icon icon={faEnvelope} />
            </Button>
            <div>
              <Text fw={500}>{t("Email")}</Text>
              <Text color={"gray"} variant="link" component="a" href="mailto:support@arc360.com.au">
                support@arc360.com.au
              </Text>
            </div>
          </Group>
        </SimpleGrid>
      </div>

      <form className={classes.form} onSubmit={form.onSubmit(sendEmail)}>
        <Text className={classes.title}>{t("Get in Touch")}</Text>
        <ScrollArea style={{ height: "calc(100vh - 340px)" }} offsetScrollbars scrollbarSize={10}>
          <SimpleGrid cols={2} mt="xl" breakpoints={[{ maxWidth: "sm", cols: 1 }]}>
            <TextInput
              label="Name"
              placeholder="Your name"
              name="name"
              {...form.getInputProps("name")}
              onChange={(event) => {
                form.setFieldValue("name", event.currentTarget.value);
                resetState();
              }}
              required
            />
            <TextInput
              label="Phone Number"
              placeholder="Your phone number"
              name=" phoneNumber"
              {...form.getInputProps("phoneNumber")}
              onChange={(event) => {
                form.setFieldValue("phoneNumber", event.currentTarget.value);
                resetState();
              }}
              required
            />
          </SimpleGrid>
          <SimpleGrid cols={2} mt="xl" breakpoints={[{ maxWidth: "sm", cols: 1 }]}>
            <TextInput
              label="Email Address"
              placeholder="Your email"
              name="email"
              {...form.getInputProps("email")}
              onChange={(event) => {
                form.setFieldValue("email", event.currentTarget.value);
                resetState();
              }}
              required
            />
            <TextInput
              label="Subject"
              placeholder="Subject"
              name="subject"
              {...form.getInputProps("subject")}
              onChange={(event) => {
                form.setFieldValue("subject", event.currentTarget.value);
                resetState();
              }}
              required
            />
          </SimpleGrid>
          <Textarea
            mt="xl"
            label="Message"
            placeholder="Your message"
            maxRows={10}
            minRows={3}
            autosize
            name="message"
            {...form.getInputProps("message")}
            onChange={(event) => {
              form.setFieldValue("message", event.currentTarget.value);
              resetState();
            }}
          />

          <Flex align={"center"} mt="xl" direction={"column"} gap={10}>
            <Button type="submit" loading={sending}>
              {t("Send")}
            </Button>
            {error.message !== "" ? (
              <Text size={theme.fontSizes?.sm} color={error.isError ? "red" : "green"}>
                {error.message}
              </Text>
            ) : null}
          </Flex>
        </ScrollArea>
      </form>
    </div>
  );
}
